import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Event, IEvent, Position, User, ApiResponse, Day, Debt, HOST } from './types';
import { xhr } from './util';

export default function EventModal({ 
  origin, 
  theme,
  setEvent,
  setCalendar,
  setEventOrigin
}: { 
  origin: Position,
  theme: string,
  setEvent: React.Dispatch<React.SetStateAction<IEvent|null>>
  setCalendar: React.Dispatch<React.SetStateAction<Day[][]>>,
  setEventOrigin: React.Dispatch<React.SetStateAction<Position>>
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { event, user } = location.state || {};
  const [saved, setSaved] = useState<boolean>(false);
  const [localEvent, setLocalEvent] = useState<IEvent>(event);
  const [processing, setProcessing] = useState<boolean>(false);
  const validatedEvent = useMemo(() => {
    return {
      ...localEvent,
      amount: (localEvent.amount == '' || Number.isNaN(localEvent.amount) ? 0.0 : +localEvent.amount) 
    };
  }, [localEvent]);
  useEffect(() => {
    if (event) {
      setLocalEvent(event);
    }
  }, [event]);
  useEffect(() => {
    const waitForElement = () => {
      const calendarEvent = document.getElementById(`event-${event.id}`);
      if (calendarEvent) {
        setEvent(event);
        setLocalEvent(event);
      } else {
        window.requestAnimationFrame(waitForElement);
      }
    }
    waitForElement();
  }, []);
  const eventRef = useRef<HTMLDivElement|null>(null);
  const saveThisEvent = useCallback(() => {
    setProcessing(true);
    xhr({
      method: 'PUT',
      url: HOST + '/save-event',
      body: validatedEvent
    }).then((res: ApiResponse) => {
      if (res.success) {
        setCalendar(res.data as Day[][]);
        
      }
      setProcessing(false);
    });
  }, [localEvent, origin]);
  const saveAllTheseEvents = useCallback(() => {
    setProcessing(true);
    xhr({
      method: 'PUT',
      url: HOST + '/save-event?all=true',
      body: validatedEvent
    }).then((res: ApiResponse) => {
      if (res.success) {
        setCalendar(res.data as Day[][]);
        
      }
      setProcessing(false);
    });
  }, [localEvent, origin]);
  const deleteThisEvent = useCallback(() => {
    setProcessing(true);
    xhr({
      method: 'DELETE',
      url: HOST + '/delete-event?id=' + localEvent.id
    }).then((res: ApiResponse) => {
      if (res.success) {
        setCalendar(res.data as Day[][]);
        navigate("/");
      }
      setProcessing(false);
    });
  }, [localEvent]);
  const deleteAllTheseEvents = useCallback(() => {
    setProcessing(true);
    xhr({
      method: 'DELETE',
      url: HOST + '/delete-event?recurrenceId=' + localEvent.recurrenceId
    }).then((res: ApiResponse) => {
      if (res.success) {
        setCalendar(res.data as Day[][]);
        navigate("/");
      }
      setProcessing(false);
    });
  }, [localEvent]);
  const cludeThisEvent = useCallback(() => {
    setProcessing(true);
    xhr({
      method: 'PUT',
      url: HOST + '/clude-event?id=' + localEvent.id
    }).then((res: ApiResponse) => {
      if (res.success) {
        setCalendar(res.data as Day[][]);
        setLocalEvent({
          ...localEvent,
          exclude: !localEvent.exclude
        });
      }
      setProcessing(false);
    });
  }, [localEvent]);

  if (!origin.left && !origin.top) return null;

	return <div className={"modal " + theme}
    style={{
      left: origin.left + 'px', 
      top: origin.top + 'px',
      border: saved ? '2px solid lawngreen' : 'none'
    }}>
    <div className='id modal-content' id="event-edit">
      <div id="modal-event" ref={eventRef}>
        <input disabled className="hidden" name="id" 
          value={localEvent.id} />
        <input disabled className="hidden" 
          name="recurrenceId" 
          value={localEvent.recurrenceId}/>
        <div className="upper-div-collection">
          <div id="summary-div">
            <label>Summary</label>
            <textarea className="this focusable"
              value={localEvent.summary} 
              name="summary"
              onChange={(e) => setLocalEvent({ ...localEvent, summary: e.target.value })}  />
          </div>
          <div id="debt-id">
            <label>Debt Id</label>
            <select name="debtId" value={localEvent.debtId ?? ""} onChange={(e) => setLocalEvent({ ...localEvent, debtId: e.target.value.length ? e.target.value : null })}>
              <option value="">None</option>
              {
                user.account.debts.map((debt: Debt) => <option key={debt.id} value={debt.id}>{debt.name}</option>)
              }
            </select>
          </div>
          <div id="frequency-container">
            <label>Frequency</label>
            <div className="td select-container focusable">
              <select value={localEvent.frequency}
                onChange={(e) => setLocalEvent({ ...localEvent, frequency: e.target.value })}>
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">BiWeekly</option>
                <option value="daily">Daily</option>
              </select>
            </div>
          </div>
        </div>
        <div id="time-container">
          <div id="amount-div">
            <label>Amount</label>
            <input className={`this focusable ${localEvent.amount < 0 ? 'negative' : 'positive'}`} 
              type="text" id="event-amount" name="amount"
              data-type="number"
              value={localEvent.amount}
              onChange={(e) => setLocalEvent({ ...localEvent, amount: e.target.value })} />
          </div>
          
          <div id="date-container">
            <div>
              <label>Date</label>
              <input name="date" className="td focusable" type="date" 
                value={localEvent.date.split("T")[0]}
                onChange={(e) => setLocalEvent({ ...localEvent, date: e.target.value })} />
            </div>
            <div>
              <label>End Date</label>
              <input name="recurrenceEndDate" className="td focusable" type="date" 
                value={localEvent.recurrenceEndDate.split("T")[0]}
                onChange={(e) => setLocalEvent({ ...localEvent, recurrenceEndDate: e.target.value })} />
            </div>
          </div>
        </div>
      </div>
      <div className="button-footer">
        {
          processing ? <div className="processing">
            <div className="loading"></div>
          </div> : null
        }
        <button id="save-this-event" className="focusable" onClick={saveThisEvent}>save</button>
        <button id="save-this-and-future-events" className="focusable" onClick={saveAllTheseEvents}>save all</button>
        <button id="clude-this-event" className="focusable" onClick={cludeThisEvent}>{ localEvent.exclude ? 'include' : 'exclude' }</button>
        <button id="delete-this-event" className="focusable" onClick={deleteThisEvent}>delete</button>
        <button id="delete-all-these-events" className="focusable" onClick={deleteAllTheseEvents}>delete all</button>
      </div>
    </div>
	</div> 
}